import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CTAButton from '../../components/base/CTAButton';
import { useSEO, generateArticleSchema } from '../../utils/seo';

export default function TechniekVeiligheidPage() {
  useSEO({
    title: 'Cinewall met haard | Techniek, veiligheid en juiste keuze',
    description: 'Cinewall met haard laten maken? Lees alles over techniek, veiligheid, plaatsing en veelgemaakte fouten. Gratis advies en offerte.',
    keywords: 'cinewall techniek, cinewall veiligheid, haard plaatsing, cinewall specialist',
    canonical: `${import.meta.env.VITE_SITE_URL}/cinewall-met-haard-techniek`,
    schema: generateArticleSchema(
      'Cinewall met haard: techniek, veiligheid en juiste keuze',
      'Uitgebreide gids over technische aspecten, veiligheid en materiaalkeuze bij cinewalls met haard',
      '/cinewall-met-haard-techniek',
      '2024-01-15',
      '2024-01-15'
    ),
  });

  const technicalAspects = [
    {
      icon: 'ri-temp-hot-line',
      title: 'Warmteontwikkeling',
      description: 'Correcte ventilatie en afstand tot tv voor optimale werking',
    },
    {
      icon: 'ri-tools-line',
      title: 'Constructie',
      description: 'Stabiele opbouw die extra gewicht van haard en tv draagt',
    },
    {
      icon: 'ri-air-condition-line',
      title: 'Ventilatie',
      description: 'Onzichtbare luchtcirculatie voor langere levensduur',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Materialen',
      description: 'Hittebestendige materialen die niet verkleuren',
    },
  ];

  const commonMistakes = [
    'Te weinig ventilatie rondom de haard',
    'Haard te dicht onder de tv geplaatst',
    'Gebruik van onjuiste materialen',
    'Geen rekening met onderhoud',
    'Esthetiek boven veiligheid',
    'Onvoldoende warmteafvoer',
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background to-background-light"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="text-accent text-sm font-semibold uppercase tracking-wider">Techniek & Veiligheid</span>
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-primary mt-4 mb-6 leading-tight">
              Cinewall met haard:<br />techniek, veiligheid en juiste keuze
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Een cinewall met haard is technisch complexer dan een standaard cinewall. Naast design spelen veiligheid, warmteontwikkeling, plaatsing en materiaalkeuze een grote rol. Een verkeerde opbouw kan leiden tot storingen, onveilige situaties of een minder mooi eindresultaat.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Technical Matters */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">
              Waarom techniek bij een cinewall met haard zo belangrijk is
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
              Een haard in een cinewall betekent extra aandacht voor warmte, elektronica, ventilatie en constructie. Zonder correcte techniek kan dit leiden tot problemen.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technicalAspects.map((aspect, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-background-light p-8 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-accent/10 rounded-full mb-6">
                  <i className={`${aspect.icon} text-2xl text-accent`}></i>
                </div>
                <h3 className="text-xl font-semibold text-primary mb-3">{aspect.title}</h3>
                <p className="text-gray-600 leading-relaxed">{aspect.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Electric vs Water Vapor */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">
              Elektrische vs waterdamp haard – technisch verschil
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-2xl shadow-lg"
            >
              <h3 className="text-2xl font-serif font-bold text-primary mb-6">Elektrische haard</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Kan warmte afgeven (optioneel)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Vereist ventilatieopeningen</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Extra afstand tot tv noodzakelijk</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Zeer stabiel en onderhoudsarm</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-2xl shadow-lg"
            >
              <h3 className="text-2xl font-serif font-bold text-primary mb-6">Waterdamp haard</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Geen warmteafgifte</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Vereist waterreservoir of vaste aansluiting</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Extra aandacht voor vochtbeheersing</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Zeer realistisch vlambeeld</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Wij adviseren welk type technisch het beste past bij uw woning en gebruik.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Safety & Placement */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">
                Veilige plaatsing van haard en tv
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                De plaatsing van haard en tv bepaalt veiligheid én comfort.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Minimale afstand tussen haard en tv</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Warmtebuffer tussen haard en meubel</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Correcte kijkhoogte van de tv</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Visuele balans in het ontwerp</span>
                </div>
              </div>
              <p className="text-gray-600 mt-6 leading-relaxed">
                Zo voorkomt u klachten zoals te warme tv's of oncomfortabele kijkhoogtes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://public.readdy.ai/ai/img_res/976327650402dc08bad06c9a9882db3b.jpg"
                alt="Veilige plaatsing cinewall met haard"
                className="w-full h-[600px] object-cover object-top rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">
              Veelgemaakte fouten bij cinewalls met haard
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Wij zien regelmatig problemen bij verkeerd opgebouwde cinewalls. Door te kiezen voor een specialist voorkomt u deze fouten.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {commonMistakes.map((mistake, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-md flex items-start"
              >
                <i className="ri-close-circle-line text-red-500 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-700">{mistake}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials & Maintenance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-serif font-bold text-primary mb-6">
                Materiaalkeuze rond de haard
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Niet elk materiaal is geschikt rondom een haard. Wij gebruiken uitsluitend:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Hittebestendige MDF-soorten</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Specifieke panelen rond de haard</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Afwerkingen die niet verkleuren</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Constructies die werking minimaliseren</span>
                </li>
              </ul>
              <p className="text-gray-600 mt-6 leading-relaxed">
                Zo blijft uw cinewall strak, ook na jarenlang gebruik.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-serif font-bold text-primary mb-6">
                Onderhoud en levensduur
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Een goed opgebouwde cinewall met haard is onderhoudsarm. Wij geven duidelijke instructies over:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Schoonmaken van de haard</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Waterbeheer (bij waterdamp)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Veilig gebruik</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Instellingen en bediening</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
              Wilt u zeker weten dat uw cinewall met haard technisch veilig en perfect wordt uitgevoerd?
            </h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Vraag vrijblijvend advies of een offerte aan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <CTAButton to="/cinewall-offerte" variant="secondary">
                Vraag gratis offerte aan
              </CTAButton>
              <CTAButton to="/cinewall-showroom" className="bg-white hover:bg-gray-100 text-accent">
                Plan showroomafspraak
              </CTAButton>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}